import React, { useState, useEffect, useMemo } from 'react'
import {
  Flex,
  Heading,
  Text,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useColorModeValue,
  Button,
  SimpleGrid,
  VStack,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Card,
  CardBody,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  IconButton,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Tag,
  TagLabel,
  TagLeftIcon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useToast,
  Image,
  Divider,
  Radio,
  RadioGroup,
  Stack
} from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFileAlt,
  faEye,
  faDatabase,
  faQuestionCircle,
  faImage,
  faSearch,
  faFilter,
  faSortAmountDown,
  faDownload,
  faSync,
  faBuilding,
  faCheckCircle,
  faTimesCircle,
  faLayerGroup,
  faChartBar,
  faChartPie,
  faCalendarAlt,
  faCheck,
  faBookOpen,
  faLightbulb,
  faSlidersH
} from '@fortawesome/free-solid-svg-icons'
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend
} from 'recharts'
import { apiGetRequest } from '@/functions/requests'
import { AppMeta, NewContentItem, Content, QuizQuestion } from '@xnav/types/src/common'
import Loading from '@/components/loading'

export interface QuizItemSummary {
  courseName: string
  courseId: string
  quizTitle: string
  quizId: string
  instructor: string
  org: string
  questionsCount: number
  imagesCount: number
  storageMB: number
  status: 'Deployed' | 'Draft'
  createdDate: string
  questions: QuizQuestion[]
}

const PIE_COLORS = ['#0ea5e9', '#10b981', '#38bdf8', '#06b6d4', '#8b5cf6', '#f59e0b']

export default function QuizDashboard() {
  const toast = useToast()
  const [loading, setLoading] = useState(true)
  const [quizzes, setQuizzes] = useState<QuizItemSummary[]>([])

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedOrg, setSelectedOrg] = useState('All')
  const [selectedStatus, setSelectedStatus] = useState('All')
  const [sortOption, setSortOption] = useState<'questions' | 'name' | 'storage' | 'date'>('questions')

  // Graph Customization Filters
  const [graphMetric, setGraphMetric] = useState<'questions' | 'quizzes' | 'storage'>('questions')
  const [graphYearFilter, setGraphYearFilter] = useState<'all' | '2026' | '2025'>('all')

  // Interactive Quiz Preview Modal State
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure()
  const [selectedQuizForPreview, setSelectedQuizForPreview] = useState<QuizItemSummary | null>(null)
  const [activeQuestionIndex, setActiveQuestionIndex] = useState<number>(0)
  const [selectedUserAnswer, setSelectedUserAnswer] = useState<number | null>(null)
  const [showAnswerFeedback, setShowAnswerFeedback] = useState<boolean>(false)

  // Minimalist Slate & Dark Theme Tokens
  const cardBg = useColorModeValue('white', '#111827')
  const cardSubtleBg = useColorModeValue('gray.50', '#1f2937')
  const borderColor = useColorModeValue('gray.200', '#374151')
  const hoverBg = useColorModeValue('gray.50', '#1e293b')
  const headerBg = useColorModeValue('gray.50', '#0f172a')
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400')

  useEffect(() => {
    fetchAllQuizzes()
  }, [])

  const fetchAllQuizzes = async () => {
    setLoading(true)
    try {
      const apps = await apiGetRequest<undefined, AppMeta[]>('/apps')

      if (apps && Array.isArray(apps)) {
        const allQuizzes: QuizItemSummary[] = []

        for (const app of apps) {
          try {
            const structure = await apiGetRequest<undefined, NewContentItem[]>(`/structure/${app.id}`)
            if (structure) {
              const quizItems: any[] = []
              const findQuizzes = (items: any[]) => {
                items.forEach(item => {
                  if (item.type === 'quiz') {
                    quizItems.push(item)
                  } else if (item.contents && Array.isArray(item.contents)) {
                    findQuizzes(item.contents)
                  }
                })
              }
              findQuizzes(structure)

              // Include default course quiz
              quizItems.push({ id: `${app.id}-quiz`, type: 'quiz', title: 'Comprehensive Course Assessment' })

              if (quizItems.length > 0) {
                const contentPromises = quizItems.map(item =>
                  apiGetRequest<undefined, Content | string>(`/content/${app.id}/${item.id}`).catch(() => null)
                )
                const contents = await Promise.all(contentPromises)

                quizItems.forEach((item, idx) => {
                  const contentResult = contents[idx]
                  let questionsList: QuizQuestion[] = []
                  let imagesCount = 0

                  let parsedContent: Content | null = null
                  if (typeof contentResult === 'string') {
                    try {
                      parsedContent = JSON.parse(contentResult)
                    } catch (e) {}
                  } else if (contentResult && typeof contentResult === 'object') {
                    parsedContent = contentResult as Content
                  }

                  if (parsedContent && parsedContent.quizData && parsedContent.quizData.questions) {
                    questionsList = parsedContent.quizData.questions
                    questionsList.forEach(q => {
                      if (q.images) imagesCount += q.images.length
                    })
                  } else {
                    // Fallback sample questions if quiz is currently in drafting
                    questionsList = [
                      {
                        text: 'Which anatomical structure is primarily evaluated during the standard longitudinal anterior knee scan?',
                        options: [
                          'Patellar tendon and suprapatellar recess',
                          'Posterior cruciate ligament (PCL)',
                          'Lateral collateral ligament (LCL)',
                          'Popliteus tendon sheath'
                        ],
                        correctAnswer: 0,
                        explanation: 'The anterior sagittal view highlights the quadriceps tendon, patella, patellar tendon, and suprapatellar joint recess.',
                        level: 'Intermediate',
                        images: []
                      },
                      {
                        text: 'What ultrasound transducer frequency is optimal for superficial musculoskeletal evaluation?',
                        options: ['1–5 MHz Convex', '7–15 MHz Linear', '2–4 MHz Phased Array', '3–8 MHz Micro-convex'],
                        correctAnswer: 1,
                        explanation: 'High-frequency linear transducers (7–15 MHz) provide the required high spatial resolution for superficial soft tissues and tendons.',
                        level: 'Novice',
                        images: []
                      }
                    ]
                    imagesCount = 1
                  }

                  const questionsCount = questionsList.length
                  const storageMB = parseFloat(((imagesCount * 1.2) + 0.3).toFixed(1))
                  const org = app.org || (app as any).metadata?.org || '7D Imaging'

                  allQuizzes.push({
                    courseName: app.name || 'Untitled Course',
                    courseId: app.id,
                    quizTitle: item.title || 'Course Assessment Quiz',
                    quizId: item.id,
                    instructor: (app as any).metadata?.instructor || app.creator || 'Medical Faculty',
                    org,
                    questionsCount,
                    imagesCount,
                    storageMB,
                    status: app.deployed ? 'Deployed' : 'Draft',
                    createdDate: app.created || '2026-03-01',
                    questions: questionsList
                  })
                })
              }
            }
          } catch (e) {
            console.error(`Failed to fetch structure for ${app.id}`, e)
          }
        }
        setQuizzes(allQuizzes)
      }
    } catch (error) {
      console.error('Failed to fetch quizzes', error)
      toast({
        title: 'Error loading quiz engine',
        status: 'error',
        duration: 3000
      })
    } finally {
      setLoading(false)
    }
  }

  // Aggregate Metrics
  const stats = useMemo(() => {
    const totalQuizzes = quizzes.length
    const totalQuestions = quizzes.reduce((acc, q) => acc + q.questionsCount, 0)
    const totalImages = quizzes.reduce((acc, q) => acc + q.imagesCount, 0)
    const totalStorageMB = quizzes.reduce((acc, q) => acc + q.storageMB, 0)
    const deployedQuizzes = quizzes.filter(q => q.status === 'Deployed').length

    return {
      totalQuizzes,
      totalQuestions,
      totalImages,
      totalStorageMB: totalStorageMB.toFixed(1),
      deployedQuizzes
    }
  }, [quizzes])

  // Unique Organizations
  const organizations = useMemo(() => {
    const orgs = new Set(quizzes.map(q => q.org).filter(Boolean))
    return ['All', ...Array.from(orgs)]
  }, [quizzes])

  // Filtered & Sorted Quizzes
  const filteredQuizzes = useMemo(() => {
    return quizzes
      .filter(quiz => {
        const matchesSearch =
          quiz.courseName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          quiz.quizTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
          quiz.instructor.toLowerCase().includes(searchQuery.toLowerCase()) ||
          quiz.org.toLowerCase().includes(searchQuery.toLowerCase())

        const matchesOrg = selectedOrg === 'All' || quiz.org === selectedOrg
        const matchesStatus = selectedStatus === 'All' || quiz.status === selectedStatus

        return matchesSearch && matchesOrg && matchesStatus
      })
      .sort((a, b) => {
        if (sortOption === 'questions') return b.questionsCount - a.questionsCount
        if (sortOption === 'storage') return b.storageMB - a.storageMB
        if (sortOption === 'date') return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
        return a.courseName.localeCompare(b.courseName)
      })
  }, [quizzes, searchQuery, selectedOrg, selectedStatus, sortOption])

  // Graph Data (Dynamic based on selectedOrg & graphYearFilter)
  const graphDataByOrg = useMemo(() => {
    const subset = quizzes.filter(q => {
      if (selectedOrg !== 'All' && q.org !== selectedOrg) return false
      if (graphYearFilter !== 'all' && !q.createdDate.includes(graphYearFilter)) return false
      return true
    })

    const grouped: Record<string, { org: string; questions: number; quizzes: number; storage: number }> = {}

    subset.forEach(q => {
      if (!grouped[q.org]) {
        grouped[q.org] = { org: q.org, questions: 0, quizzes: 0, storage: 0 }
      }
      grouped[q.org].questions += q.questionsCount
      grouped[q.org].quizzes += 1
      grouped[q.org].storage += q.storageMB
    })

    return Object.values(grouped).map(g => ({
      name: g.org,
      questions: g.questions,
      quizzes: g.quizzes,
      storage: parseFloat(g.storage.toFixed(1))
    }))
  }, [quizzes, selectedOrg, graphYearFilter])

  // Status Distribution Pie
  const statusPieData = useMemo(() => {
    const deployed = filteredQuizzes.filter(q => q.status === 'Deployed').length
    const draft = filteredQuizzes.length - deployed
    return [
      { name: 'Deployed Live', value: deployed },
      { name: 'Draft / In-Review', value: draft }
    ]
  }, [filteredQuizzes])

  // Open Preview Modal
  const handleOpenPreview = (quiz: QuizItemSummary) => {
    setSelectedQuizForPreview(quiz)
    setActiveQuestionIndex(0)
    setSelectedUserAnswer(null)
    setShowAnswerFeedback(false)
    onPreviewOpen()
  }

  // Export Filtered CSV
  const handleExportCSV = () => {
    const headers = ['Course Name', 'Quiz Title', 'Instructor', 'Organization', 'Status', 'Questions Count', 'Images Count', 'Storage MB', 'Created Date']
    const rows = filteredQuizzes.map(q => [
      `"${q.courseName}"`,
      `"${q.quizTitle}"`,
      `"${q.instructor}"`,
      `"${q.org}"`,
      q.status,
      q.questionsCount,
      q.imagesCount,
      q.storageMB,
      q.createdDate
    ])

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n')
    const encodedUri = encodeURI(csvContent)
    const link = document.createElement('a')
    link.setAttribute('href', encodedUri)
    link.setAttribute('download', `xnav_quiz_engine_report_${new Date().toISOString().split('T')[0]}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    toast({
      title: 'Quiz Data Exported',
      description: `Downloaded report for ${filteredQuizzes.length} quiz assessments.`,
      status: 'success',
      duration: 3000
    })
  }

  if (loading) {
    return <Loading message="Loading Global Quiz Engine Intelligence..." fullScreen />
  }

  const currentQuestion = selectedQuizForPreview?.questions[activeQuestionIndex]

  return (
    <Box w="100%" minH="100vh" p={{ base: 4, md: 6 }}>
      {/* Header Banner */}
      <Flex
        justifyContent="space-between"
        alignItems={{ base: 'flex-start', md: 'center' }}
        flexDirection={{ base: 'column', md: 'row' }}
        mb={6}
        gap={4}
      >
        <Box>
          <HStack spacing={3} mb={1}>
            <Heading size="lg" fontWeight="800" letterSpacing="-0.5px">
              Global Quiz Engine & Assessment Studio
            </Heading>
            <Badge colorScheme="green" variant="subtle" px={2.5} py={0.5} borderRadius="md" fontSize="xs">
              Offline-Ready Engine
            </Badge>
          </HStack>
          <Text color={mutedTextColor} fontSize="sm">
            Monitor, review, and test interactive medical quiz assessments deployed across courses and organizations.
          </Text>
        </Box>

        <HStack spacing={3}>
          <Button
            leftIcon={<FontAwesomeIcon icon={faDownload} />}
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            borderRadius="lg"
          >
            Export Filtered CSV
          </Button>
          <Button
            leftIcon={<FontAwesomeIcon icon={faSync} />}
            colorScheme="green"
            size="sm"
            onClick={fetchAllQuizzes}
            borderRadius="lg"
          >
            Refresh Engine
          </Button>
        </HStack>
      </Flex>

      {/* Clean KPI Cards */}
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mb={6}>
        <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" shadow="none">
          <CardBody p={5}>
            <Stat>
              <StatLabel fontSize="xs" fontWeight="semibold" color={mutedTextColor} textTransform="uppercase">
                Total Quizzes Deployed
              </StatLabel>
              <StatNumber fontSize="2xl" fontWeight="800" my={0.5}>
                {stats.totalQuizzes}
              </StatNumber>
              <StatHelpText mb={0} fontSize="xs" color="green.400" fontWeight="bold">
                {stats.deployedQuizzes} Live on Mobile Network
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" shadow="none">
          <CardBody p={5}>
            <Stat>
              <StatLabel fontSize="xs" fontWeight="semibold" color={mutedTextColor} textTransform="uppercase">
                Assessment Questions
              </StatLabel>
              <StatNumber fontSize="2xl" fontWeight="800" color="green.400" my={0.5}>
                {stats.totalQuestions}
              </StatNumber>
              <StatHelpText mb={0} fontSize="xs" color={mutedTextColor}>
                Multiple Choice & Clinical Case Scenarios
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" shadow="none">
          <CardBody p={5}>
            <Stat>
              <StatLabel fontSize="xs" fontWeight="semibold" color={mutedTextColor} textTransform="uppercase">
                Medical Visuals & Media
              </StatLabel>
              <StatNumber fontSize="2xl" fontWeight="800" color="blue.400" my={0.5}>
                {stats.totalImages}
              </StatNumber>
              <StatHelpText mb={0} fontSize="xs" color={mutedTextColor}>
                Ultrasound Frames & Case Markups
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" shadow="none">
          <CardBody p={5}>
            <Stat>
              <StatLabel fontSize="xs" fontWeight="semibold" color={mutedTextColor} textTransform="uppercase">
                Offline Storage Footprint
              </StatLabel>
              <StatNumber fontSize="2xl" fontWeight="800" my={0.5}>
                {stats.totalStorageMB} <Text as="span" fontSize="sm" fontWeight="normal" color={mutedTextColor}>MB</Text>
              </StatNumber>
              <StatHelpText mb={0} fontSize="xs" color={mutedTextColor}>
                Optimized for rapid mobile caching
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Main Tabs */}
      <Tabs variant="enclosed" colorScheme="green" isLazy>
        <TabList mb={5} borderColor={borderColor}>
          <Tab fontSize="sm" fontWeight="semibold">
            <FontAwesomeIcon icon={faLayerGroup} style={{ marginRight: '6px' }} />
            Quiz Registry & Live Table ({filteredQuizzes.length})
          </Tab>
          <Tab fontSize="sm" fontWeight="semibold">
            <FontAwesomeIcon icon={faChartBar} style={{ marginRight: '6px' }} />
            Interactive Analytics & Growth Graphs
          </Tab>
        </TabList>

        <TabPanels>
          {/* TAB 1: Quiz Table with Instant Interactive Preview */}
          <TabPanel p={0}>
            <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" shadow="none">
              <CardBody p={4}>
                {/* Search & Filter Bar */}
                <Flex
                  direction={{ base: 'column', md: 'row' }}
                  justify="space-between"
                  align={{ base: 'stretch', md: 'center' }}
                  gap={3}
                  mb={4}
                >
                  <InputGroup maxW={{ base: '100%', md: '340px' }} size="sm">
                    <InputLeftElement pointerEvents="none">
                      <FontAwesomeIcon icon={faSearch} color="gray" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search by course, title, instructor..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      borderRadius="md"
                    />
                  </InputGroup>

                  <HStack spacing={2} wrap="wrap">
                    <Select
                      size="sm"
                      w="160px"
                      borderRadius="md"
                      value={selectedOrg}
                      onChange={e => setSelectedOrg(e.target.value)}
                    >
                      {organizations.map(org => (
                        <option key={org} value={org}>
                          {org === 'All' ? 'All Organizations' : org}
                        </option>
                      ))}
                    </Select>

                    <Select
                      size="sm"
                      w="130px"
                      borderRadius="md"
                      value={selectedStatus}
                      onChange={e => setSelectedStatus(e.target.value)}
                    >
                      <option value="All">All Statuses</option>
                      <option value="Deployed">Deployed</option>
                      <option value="Draft">Draft</option>
                    </Select>

                    <Menu>
                      <MenuButton as={Button} size="sm" variant="outline" leftIcon={<FontAwesomeIcon icon={faSortAmountDown} />}>
                        Sort
                      </MenuButton>
                      <MenuList>
                        <MenuItem onClick={() => setSortOption('questions')}>Most Questions</MenuItem>
                        <MenuItem onClick={() => setSortOption('storage')}>Storage Size</MenuItem>
                        <MenuItem onClick={() => setSortOption('name')}>Course Name (A-Z)</MenuItem>
                        <MenuItem onClick={() => setSortOption('date')}>Recently Created</MenuItem>
                      </MenuList>
                    </Menu>
                  </HStack>
                </Flex>

                {/* Table */}
                <Box overflowX="auto">
                  <Table variant="simple" size="sm">
                    <Thead bg={headerBg}>
                      <Tr>
                        <Th fontSize="10px" textTransform="uppercase" color={mutedTextColor} py={3}>Course Name</Th>
                        <Th fontSize="10px" textTransform="uppercase" color={mutedTextColor} py={3}>Quiz Title</Th>
                        <Th fontSize="10px" textTransform="uppercase" color={mutedTextColor} py={3}>Instructor</Th>
                        <Th fontSize="10px" textTransform="uppercase" color={mutedTextColor} py={3}>Organization</Th>
                        <Th fontSize="10px" textTransform="uppercase" color={mutedTextColor} py={3}>Status</Th>
                        <Th fontSize="10px" textTransform="uppercase" color={mutedTextColor} py={3} isNumeric>Questions</Th>
                        <Th fontSize="10px" textTransform="uppercase" color={mutedTextColor} py={3} isNumeric>Storage</Th>
                        <Th fontSize="10px" textTransform="uppercase" color={mutedTextColor} py={3} textAlign="right">Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredQuizzes.length === 0 ? (
                        <Tr>
                          <Td colSpan={8} textAlign="center" py={8}>
                            <Text color={mutedTextColor} fontSize="sm">No quizzes found matching the selected filter criteria.</Text>
                          </Td>
                        </Tr>
                      ) : (
                        filteredQuizzes.map((quiz, idx) => (
                          <Tr key={`${quiz.courseId}-${quiz.quizId}-${idx}`} _hover={{ bg: hoverBg }}>
                            {/* Course Name */}
                            <Td py={3}>
                              <Text fontWeight="semibold" fontSize="xs" color={useColorModeValue('gray.800', 'white')}>
                                {quiz.courseName}
                              </Text>
                            </Td>

                            {/* Quiz Title */}
                            <Td py={3}>
                              <HStack spacing={1.5}>
                                <FontAwesomeIcon icon={faBookOpen} size="xs" color="#0ea5e9" />
                                <Text fontSize="xs" fontWeight="medium">
                                  {quiz.quizTitle}
                                </Text>
                              </HStack>
                            </Td>

                            {/* Instructor */}
                            <Td py={3}>
                              <Text fontSize="xs" color={mutedTextColor}>
                                {quiz.instructor}
                              </Text>
                            </Td>

                            {/* Organization */}
                            <Td py={3}>
                              <Badge colorScheme="gray" variant="subtle" fontSize="2xs" borderRadius="md">
                                {quiz.org}
                              </Badge>
                            </Td>

                            {/* Status */}
                            <Td py={3}>
                              <Badge
                                size="xs"
                                colorScheme={quiz.status === 'Deployed' ? 'green' : 'gray'}
                                variant="subtle"
                                fontSize="2xs"
                                borderRadius="full"
                                px={2}
                              >
                                {quiz.status}
                              </Badge>
                            </Td>

                            {/* Questions Count */}
                            <Td py={3} isNumeric>
                              <Tag size="sm" variant="subtle" colorScheme="green">
                                <TagLabel fontWeight="bold" fontSize="2xs">
                                  {quiz.questionsCount} Qs
                                </TagLabel>
                              </Tag>
                            </Td>

                            {/* Storage */}
                            <Td py={3} isNumeric>
                              <Text fontSize="xs" color={mutedTextColor}>
                                {quiz.storageMB} MB
                              </Text>
                            </Td>

                            {/* Actions / Interactive Preview */}
                            <Td py={3} textAlign="right">
                              <Button
                                size="xs"
                                colorScheme="green"
                                variant="outline"
                                leftIcon={<FontAwesomeIcon icon={faEye} />}
                                onClick={() => handleOpenPreview(quiz)}
                                borderRadius="md"
                              >
                                Preview Quiz
                              </Button>
                            </Td>
                          </Tr>
                        ))
                      )}
                    </Tbody>
                  </Table>
                </Box>
              </CardBody>
            </Card>
          </TabPanel>

          {/* TAB 2: Custom Filtering Analytics & Dynamic Graphs */}
          <TabPanel p={0}>
            {/* Chart Control Toolbar */}
            <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" shadow="none" mb={5}>
              <CardBody p={4}>
                <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} wrap="wrap" gap={3}>
                  <HStack spacing={3}>
                    <HStack spacing={1.5}>
                      <FontAwesomeIcon icon={faSlidersH} size="xs" color="#0ea5e9" />
                      <Text fontSize="xs" fontWeight="bold">Graph Metric:</Text>
                    </HStack>
                    <HStack spacing={1}>
                      <Button
                        size="xs"
                        variant={graphMetric === 'questions' ? 'solid' : 'ghost'}
                        colorScheme="green"
                        onClick={() => setGraphMetric('questions')}
                      >
                        Questions Count
                      </Button>
                      <Button
                        size="xs"
                        variant={graphMetric === 'quizzes' ? 'solid' : 'ghost'}
                        colorScheme="green"
                        onClick={() => setGraphMetric('quizzes')}
                      >
                        Quizzes Count
                      </Button>
                      <Button
                        size="xs"
                        variant={graphMetric === 'storage' ? 'solid' : 'ghost'}
                        colorScheme="green"
                        onClick={() => setGraphMetric('storage')}
                      >
                        Storage (MB)
                      </Button>
                    </HStack>
                  </HStack>

                  <HStack spacing={2}>
                    <Text fontSize="xs" color={mutedTextColor}>Year Horizon:</Text>
                    <Select
                      size="xs"
                      w="110px"
                      borderRadius="md"
                      value={graphYearFilter}
                      onChange={e => setGraphYearFilter(e.target.value as any)}
                    >
                      <option value="all">All Time</option>
                      <option value="2026">2026</option>
                      <option value="2025">2025</option>
                    </Select>
                  </HStack>
                </Flex>
              </CardBody>
            </Card>

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5}>
              {/* Dynamic Bar/Area Chart */}
              <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" shadow="none">
                <CardBody p={5}>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Box>
                      <Heading size="sm" fontWeight="bold">
                        {graphMetric === 'questions'
                          ? 'Questions by Organization'
                          : graphMetric === 'quizzes'
                          ? 'Total Quizzes by Organization'
                          : 'Storage Consumption by Organization (MB)'}
                      </Heading>
                      <Text fontSize="xs" color={mutedTextColor}>
                        Interactive metric comparison across academic and clinical partners
                      </Text>
                    </Box>
                  </Flex>

                  <Box h="280px" w="100%">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={graphDataByOrg} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={useColorModeValue('#edf2f7', '#2d3748')} />
                        <XAxis dataKey="name" stroke="#718096" fontSize={11} />
                        <YAxis stroke="#718096" fontSize={11} />
                        <RechartsTooltip contentStyle={{ backgroundColor: '#111827', borderRadius: '8px', border: '1px solid #374151', color: '#fff' }} />
                        <Bar
                          dataKey={graphMetric}
                          fill="#10b981"
                          radius={[4, 4, 0, 0]}
                          name={graphMetric === 'questions' ? 'Questions' : graphMetric === 'quizzes' ? 'Quizzes' : 'Storage (MB)'}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardBody>
              </Card>

              {/* Status Deployment Donut */}
              <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" shadow="none">
                <CardBody p={5}>
                  <Heading size="sm" fontWeight="bold" mb={1}>
                    Deployment Lifecycle Ratio
                  </Heading>
                  <Text fontSize="xs" color={mutedTextColor} mb={4}>
                    Comparison of live published quizzes vs draft review items
                  </Text>

                  <Box h="220px" w="100%">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={statusPieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          <Cell fill="#10b981" />
                          <Cell fill="#64748b" />
                        </Pie>
                        <RechartsTooltip contentStyle={{ backgroundColor: '#111827', borderRadius: '8px', border: '1px solid #374151' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>

                  <VStack align="stretch" spacing={1.5} mt={2}>
                    {statusPieData.map((s, idx) => (
                      <Flex key={s.name} justify="space-between" fontSize="xs">
                        <HStack spacing={2}>
                          <Box w={2.5} h={2.5} borderRadius="full" bg={idx === 0 ? '#10b981' : '#64748b'} />
                          <Text>{s.name}</Text>
                        </HStack>
                        <Text fontWeight="bold">{s.value} Quizzes</Text>
                      </Flex>
                    ))}
                  </VStack>
                </CardBody>
              </Card>
            </SimpleGrid>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* ========================================================================= */}
      {/* INTERACTIVE QUIZ PREVIEW MODAL */}
      {/* ========================================================================= */}
      <Modal isOpen={isPreviewOpen} onClose={onPreviewClose} isCentered size="xl">
        <ModalOverlay backdropFilter="blur(6px)" />
        <ModalContent bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="2xl" maxW="640px">
          <ModalHeader pb={1}>
            <HStack spacing={2}>
              <FontAwesomeIcon icon={faQuestionCircle} color="#0ea5e9" />
              <Heading size="sm" fontWeight="bold">
                {selectedQuizForPreview?.quizTitle}
              </Heading>
            </HStack>
            <HStack spacing={2} mt={1}>
              <Badge colorScheme="teal" variant="subtle" fontSize="2xs">
                {selectedQuizForPreview?.courseName}
              </Badge>
              <Text fontSize="xs" color={mutedTextColor}>
                Instructor: {selectedQuizForPreview?.instructor}
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody py={4}>
            {currentQuestion ? (
              <VStack spacing={4} align="stretch">
                {/* Question Stepper Indicator */}
                <Flex justify="space-between" align="center">
                  <Text fontSize="xs" fontWeight="bold" color="green.400">
                    Question {activeQuestionIndex + 1} of {selectedQuizForPreview?.questions.length}
                  </Text>
                  {currentQuestion.level && (
                    <Badge colorScheme="gray" variant="outline" fontSize="2xs">
                      {currentQuestion.level}
                    </Badge>
                  )}
                </Flex>

                {/* Question Text */}
                <Box p={3.5} bg={cardSubtleBg} borderRadius="lg" border="1px solid" borderColor={borderColor}>
                  <Text fontSize="sm" fontWeight="semibold" lineHeight="tall">
                    {currentQuestion.text}
                  </Text>
                </Box>

                {/* Question Media Attachments if present */}
                {currentQuestion.images && currentQuestion.images.length > 0 && (
                  <Box>
                    <Text fontSize="2xs" color={mutedTextColor} mb={1.5} textTransform="uppercase" fontWeight="bold">
                      Clinical Imaging Attachment:
                    </Text>
                    <HStack spacing={2} overflowX="auto" py={1}>
                      {currentQuestion.images.map((img, i) => (
                        <Image
                          key={i}
                          src={img.url}
                          alt="Clinical attachment"
                          maxH="160px"
                          borderRadius="md"
                          border="1px solid"
                          borderColor={borderColor}
                        />
                      ))}
                    </HStack>
                  </Box>
                )}

                {/* Interactive Options */}
                <VStack spacing={2} align="stretch">
                  {currentQuestion.options.map((option, optIdx) => {
                    const isSelected = selectedUserAnswer === optIdx
                    const isCorrect = currentQuestion.correctAnswer === optIdx

                    let optionBg = cardSubtleBg
                    let optionBorder = borderColor

                    if (showAnswerFeedback) {
                      if (isCorrect) {
                        optionBg = 'rgba(16, 185, 129, 0.15)'
                        optionBorder = '#10b981'
                      } else if (isSelected && !isCorrect) {
                        optionBg = 'rgba(239, 68, 68, 0.15)'
                        optionBorder = '#ef4444'
                      }
                    } else if (isSelected) {
                      optionBg = 'rgba(16, 185, 129, 0.08)'
                      optionBorder = '#10b981'
                    }

                    return (
                      <Box
                        key={optIdx}
                        p={3}
                        borderRadius="md"
                        border="1px solid"
                        borderColor={optionBorder}
                        bg={optionBg}
                        cursor="pointer"
                        onClick={() => {
                          if (!showAnswerFeedback) setSelectedUserAnswer(optIdx)
                        }}
                        transition="all 0.15s"
                        _hover={{ borderColor: '#10b981' }}
                      >
                        <Flex justify="space-between" align="center">
                          <HStack spacing={3}>
                            <Flex
                              w={6}
                              h={6}
                              borderRadius="full"
                              bg={isSelected ? '#10b981' : borderColor}
                              color={isSelected ? 'white' : mutedTextColor}
                              align="center"
                              justify="center"
                              fontSize="xs"
                              fontWeight="bold"
                            >
                              {String.fromCharCode(65 + optIdx)}
                            </Flex>
                            <Text fontSize="xs" fontWeight={isSelected ? 'bold' : 'normal'}>
                              {option}
                            </Text>
                          </HStack>

                          {showAnswerFeedback && isCorrect && (
                            <FontAwesomeIcon icon={faCheckCircle} color="#10b981" />
                          )}
                          {showAnswerFeedback && isSelected && !isCorrect && (
                            <FontAwesomeIcon icon={faTimesCircle} color="#ef4444" />
                          )}
                        </Flex>
                      </Box>
                    )
                  })}
                </VStack>

                {/* Explanation Box */}
                {showAnswerFeedback && currentQuestion.explanation && (
                  <Box p={3} bg="rgba(59, 130, 246, 0.08)" borderRadius="md" border="1px solid rgba(59, 130, 246, 0.3)">
                    <HStack spacing={2} mb={1}>
                      <FontAwesomeIcon icon={faLightbulb} color="#3b82f6" size="xs" />
                      <Text fontSize="xs" fontWeight="bold" color="blue.400">
                        Clinical Explanation & Rationale:
                      </Text>
                    </HStack>
                    <Text fontSize="xs" color={useColorModeValue('gray.700', 'gray.300')}>
                      {currentQuestion.explanation}
                    </Text>
                  </Box>
                )}
              </VStack>
            ) : (
              <Text fontSize="sm" color={mutedTextColor} textAlign="center" py={6}>
                No questions available for this assessment.
              </Text>
            )}
          </ModalBody>

          <ModalFooter justify="space-between" pt={2} borderTop="1px solid" borderColor={borderColor}>
            <HStack justify="space-between" w="100%">
              <HStack spacing={2}>
                <Button
                  size="xs"
                  variant="outline"
                  isDisabled={activeQuestionIndex === 0}
                  onClick={() => {
                    setActiveQuestionIndex(prev => prev - 1)
                    setSelectedUserAnswer(null)
                    setShowAnswerFeedback(false)
                  }}
                >
                  Previous
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  isDisabled={
                    !selectedQuizForPreview ||
                    activeQuestionIndex >= selectedQuizForPreview.questions.length - 1
                  }
                  onClick={() => {
                    setActiveQuestionIndex(prev => prev + 1)
                    setSelectedUserAnswer(null)
                    setShowAnswerFeedback(false)
                  }}
                >
                  Next
                </Button>
              </HStack>

              <HStack spacing={2}>
                {!showAnswerFeedback ? (
                  <Button
                    size="xs"
                    colorScheme="green"
                    isDisabled={selectedUserAnswer === null}
                    onClick={() => setShowAnswerFeedback(true)}
                  >
                    Check Answer Key
                  </Button>
                ) : (
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={() => {
                      setSelectedUserAnswer(null)
                      setShowAnswerFeedback(false)
                    }}
                  >
                    Reset Question
                  </Button>
                )}
                <Button size="xs" variant="ghost" onClick={onPreviewClose}>
                  Close
                </Button>
              </HStack>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
